import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useApiRequest } from "../Login/Hooks/useApiRequest";
import { API_BASE_URL } from "../../config";
import Orders from "./component";
import NoInternet from "../../NoInterNet/component";

const OrdersContainer = (props) => {
  const { makeApiRequest } = useApiRequest();
  const navigate = useNavigate();
  const location = useLocation();

  const token    = localStorage.getItem("UserJWTAuth");
  const deviceId = localStorage.getItem("uuid");

  // Order data
  const [orderHistory, setOrderHistory]             = useState(null);
  const [activeOrderHistory, setActiveOrderHistory] = useState(null);
  const [loader, setLoader]                         = useState(true);

  // ✅ Offline state — drives the NoInternet screen
  const [noInternet, setNoInternet] = useState(false);

  // Google link-account flow
  const [googlePopFlag, setGooglePopFlag]           = useState(false);
  const [googleOtpPopFlag, setGoogleOtpPopFlag]     = useState(false);
  const [googleSuccessFlag, setGoogleSuccessFlag]   = useState(false);
  const [googleRequestId, setGoogleRequestId]       = useState(null);

  // Google link-account form
  const [number, setNumber]               = useState("");
  const [otp, setOtp]                     = useState("");
  const [numberError, setNumberError]     = useState(false);
  const [numberErrorMsg, setNumberErrorMsg] = useState("");
  const [otpError, setOtpError]           = useState(false);
  const [otpErrorMsg, setOtpErrorMsg]     = useState("");

  // ─── Online / offline listeners ─────────────────────────────────────────────
  // Covers three scenarios:
  //   1. Already offline when page loads  → show NoInternet immediately
  //   2. Goes offline mid-session         → show NoInternet reactively
  //   3. Connection restored              → auto-retry all fetches

  useEffect(() => {
    if (!navigator.onLine) {
      setNoInternet(true);
      setLoader(false);
    }

    const goOffline = () => {
      setNoInternet(true);
    };

    const goOnline = () => {
      setNoInternet(false);
      setLoader(true);
      fetchOrderHistory();
      fetchActiveOrderHistory();
      fetchUserDetails();
    };

    window.addEventListener("offline", goOffline);
    window.addEventListener("online",  goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online",  goOnline);
    };
  }, []);

  // ─── Auth guards ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    if (localStorage.getItem("Guest_clientId")) {
      navigate("/login", {
        state: { from: location.pathname + location.search },
      });
    }
  }, []);

  // ─── Bootstrap data fetch ────────────────────────────────────────────────────

  useEffect(() => {
    if (!navigator.onLine) return;
    fetchOrderHistory();
    fetchActiveOrderHistory();
    fetchUserDetails();
  }, []);

  // ─── Shared network-error handler ────────────────────────────────────────────
  // Returns true if it was a network/connectivity error (caller should bail out)

  const handleNetworkError = (error) => {
    if (error.code === "ERR_NETWORK" || !error.response) {
      setNoInternet(true);
      setLoader(false);
      return true;
    }
    return false;
  };


    // ─── Online / offline listeners ───────────────────────────────────────────

  useEffect(() => {
    if (!navigator.onLine) {
      setNoInternet(true);
      setLoader(false);
    }

    const goOffline = () => setNoInternet(true);
    const goOnline = () => {
      setNoInternet(false);
      setLoader(true);
    };

    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  // ─── User profile ─────────────────────────────────────────────────────────────

  const fetchUserDetails = () => {
    if (!token) return;
    makeApiRequest({
      url: `${API_BASE_URL}/auth/user/profile/details`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-app-platform": "web",
        "x-app-version": "v1.0.1",
        "x-device-id": deviceId || "",
      },
      data: { request: { oauthToken: `Bearer ${token}` } },
      handleSuccess: (res) => {
        const data = res?.data?.response;
        if (data?.referCode) localStorage.setItem("referCode", data.referCode);
        setLoader(false);
      },
      handleError: (error) => {
        if (handleNetworkError(error)) return;
        setLoader(false);
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          if (localStorage.getItem("google_jwt")) setGooglePopFlag(true);
        } else {
          toast.error(error?.response?.data?.status || "Something went wrong", {
            position: "top-right",
            autoClose: 1000,
          });
        }
      },
    });
  };

  // ─── Past order history ───────────────────────────────────────────────────────

  const fetchOrderHistory = () => {
    makeApiRequest({
      url: `${API_BASE_URL}/order/order/orderhistory`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-app-platform": "web",
        "x-app-version": "v1.0.1",
        "x-device-id": deviceId || "",
      },
      data: { request: {} },
      handleSuccess: (res) => {
        setOrderHistory(res.data.response);
        setLoader(false);
      },
      handleError: (error) => {
        if (handleNetworkError(error)) return;
        setLoader(false);
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          navigate("/login", {
            state: { from: location.pathname + location.search },
          });
        } else {
          toast.error(
            error?.response?.data?.status?.code || "Failed to load orders",
            { position: "top-right", autoClose: 1000 }
          );
        }
      },
    });
  };

  // ─── Active order history ─────────────────────────────────────────────────────

  const fetchActiveOrderHistory = () => {
    makeApiRequest({
      url: `${API_BASE_URL}/consumerbe/api/v2/order/active`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-app-platform": "web",
        "x-app-version": "v1.0.1",
        "x-device-id": deviceId || "",
      },
      handleSuccess: (res) => {
        setActiveOrderHistory(res.data.data);
        setLoader(false);
      },
      handleError: (error) => {
        if (handleNetworkError(error)) return;
        setLoader(false);
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          navigate("/login", {
            state: { from: location.pathname + location.search },
          });
        } else {
          toast.error(
            error?.response?.data?.status?.code || "Failed to load active orders",
            { position: "top-right", autoClose: 1000 }
          );
        }
      },
    });
  };

  // ─── Google account linking ───────────────────────────────────────────────────

  const googleAddNumber = () => {
    if (number.length !== 10) {
      setNumberError(true);
      setNumberErrorMsg("Enter a valid mobile number");
      return;
    }
    setNumberError(false);
    makeApiRequest({
      url: `${API_BASE_URL}/auth/user/authenticate`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-app-platform": "web",
        "x-app-version": "v1.0.1",
        "x-device-id": deviceId || "",
      },
      data: { request: { mobileNo: number, googleId: localStorage.getItem("gmail") } },
      handleSuccess: (res) => {
        setGoogleRequestId(res.data.response.requestId);
        setGooglePopFlag(false);
        setGoogleOtpPopFlag(true);
      },
      handleError: (err) => {
        if (handleNetworkError(err)) return;
        const msg = err?.response?.data?.statusMessage || "Something went wrong";
        setNumberError(true);
        setNumberErrorMsg(msg);
        toast.error(msg, { position: "top-right", autoClose: 1000 });
      },
    });
  };

  const googleOtpSubmit = () => {
    if (otp.length !== 4) {
      setOtpError(true);
      return;
    }
    setOtpError(false);
    makeApiRequest({
      url: `${API_BASE_URL}/auth/user/login`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-app-platform": "web",
        "x-app-version": "v1.0.1",
        "x-device-id": deviceId || "",
      },
      data: {
        request: {
          deviceId, fcmUserToken: null,
          mobileNo: number, otp,
          requestId: googleRequestId,
          sourcePlatform: "web",
        },
      },
      handleSuccess: (res) => {
        if (res?.data?.response) {
          const response = res.data.response;
          setGoogleSuccessFlag(true);
          setGoogleOtpPopFlag(false);
          localStorage.setItem("UserJWTAuth",    response.jwt);
          localStorage.setItem("referCode",      response.referCode);
          localStorage.setItem("isUserLogged",   response.isUserLogged);
          localStorage.setItem("isGuest",        response.isGuest);
          localStorage.setItem("isTokenExpired", response.isTokenExpired);
        } else if (res?.data?.status === "FAILURE") {
          setOtpError(true);
          setOtpErrorMsg(res.data.statusMessage);
          toast.error(res.data.statusMessage, { position: "top-right", autoClose: 1000 });
        }
      },
      handleError: (err) => {
        if (handleNetworkError(err)) return;
        setOtpError(true);
      },
    });
  };

  const closePop       = () => { setGooglePopFlag(false); navigate("/"); };
  const closeOtpPop    = () => { setGoogleOtpPopFlag(false); setGooglePopFlag(true); };
  const handleNumberChange = (e) => setNumber(e.target.value);

  // ─── No internet — render NoInternet screen ───────────────────────────────────

  if (noInternet) return <NoInternet />;

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <Orders
      orderhistory={orderHistory}
      activeOrderhistory={activeOrderHistory}
      loader={loader}
      google_Pop_flag={googlePopFlag}
      setGoogle_Pop_flag={setGooglePopFlag}
      closePop={closePop}
      handle_Number={handleNumberChange}
      googleAddnumber={googleAddNumber}
      number={number}
      setNumber={setNumber}
      google_OTP_Pop_flag={googleOtpPopFlag}
      setGoogle_OTP_Pop_flag={setGoogleOtpPopFlag}
      closeOTpPop={closeOtpPop}
      numberError={numberError}
      numberErrorMsg={numberErrorMsg}
      googleOtpSubmit={googleOtpSubmit}
      setOtp={setOtp}
      otp={otp}
      otpError={otpError}
      otpErrorMsg={otpErrorMsg}
      google_success_flag={googleSuccessFlag}
      setGoogle_success_flag={setGoogleSuccessFlag}
    />
  );
};

export default OrdersContainer;
